/* eslint-disable lines-between-class-members */
import Route from 'mewe/routes/route';
import { inject as service } from '@ember/service';
import EmberObject, { action } from '@ember/object';
import Session from 'mewe/shared/session';
import { fetchJournalsBy, fetchEntriesByTeller } from 'mewe/fetchers/fetch-journals';
import ContactsApi from 'mewe/api/contacts-api';
import ProfileStore from 'mewe/stores/profile-store';
import PS from 'mewe/utils/pubsub';
import { Theme, TellerType } from 'mewe/constants';
import { ds } from 'mewe/stores/ds';
import PagesApi from 'mewe/api/pages-api';
import FunctionalUtils from 'mewe/shared/functional-utils';
import { fetchBuckets, fetchAdministrated, getPage, processPageResponse } from 'mewe/fetchers/fetch-pages';
import { removeItemIfExist } from 'mewe/stores/models/collection';
import { tracked } from '@glimmer/tracking';
import { emberSet, pick } from 'mewe/utils/fp';
import ReputationApi from 'mewe/api/reputation-api';

// Utils
function hasId(str) {
  return str.indexOf('id=') > -1;
}

function stripId(str) {
  return str.replace('id=', '');
}

export default class PublicLinkRoute extends Route {
  @service router;
  @service account;
  @service dynamicDialogs;

  @tracked context;
  @tracked theme;

  async beforeModel() {
    const { isAuthenticated } = await Session.isAuthenticated();
    // This is will redirect to the invite page if the user is not authenticated, will not allow the user to see the sub route
    if (!isAuthenticated) {
      const params = this.paramsFor('app.publicid');
      this.router.transitionTo('invite', params.id);
      return;
    }
  }

  async model(params, transition) {
    let pageOrProfile = await PagesApi.publicLink(params.id);
    this.updateContext(pageOrProfile);
    let res;
    if (pageOrProfile.user) {
      res = await this.userModel(params, pageOrProfile);
    } else {
      processPageResponse(Promise.resolve(pageOrProfile));
      res = await this.pageModel(params, transition, pageOrProfile);
    }
    return res;
  }

  redirect(model, transition) {
    if (model && !model.pageId && transition.targetName === 'app.publicid.index') {
      this.router.transitionTo('app.publicid.posts', model.params.id);
    }
    if (model && model.pageId && transition.targetName === 'app.publicid.index') {
      this.router.transitionTo('app.publicid.feed');
    }
  }

  updateContext(model) {
    if (model.page) {
      this.theme = Theme.PAGE;
      this.context = 'page';
    } else {
      this.theme = Theme.PROFILE;
      this.context = 'profile';
    }
  }

  async userModel(params, userRes) {
    const inviteId = params.id;
    let data;

    try {
      if (hasId(inviteId)) {
        data = await ContactsApi.getUserDetails({ userId: stripId(inviteId) });
      } else {
        data = userRes;
      }

      const uuid = data.user?.id || data.id;
      data.registeredAt = data.joined || data.registeredAt;
      delete data.joined;

      const contactInviteId = data.user?.contactInviteId || data.contactInviteId;

      await fetchEntriesByTeller({ id: uuid });
      await fetchJournalsBy({ id: uuid });

      ProfileStore.send('handleRawData', data);

      let isMyProfile = uuid === this.account.activeUser.id;
      let votingStatus = null;
      if (this.account.flagReputationEnabled) {
        votingStatus = await ReputationApi.getVotingStatus(uuid);
      }

      return {
        uuid,
        isMyProfile,
        inviteId: contactInviteId,
        contact: ProfileStore.getState({ uuid }),
        params,
        entries: ds.entriesByTeller.for(uuid),
        journals: ds.journalsByTeller.for(uuid),
        votingStatus,
      };
    } catch (err) {
      if (err) {
        console.log('app-profile: err: ' + JSON.stringify(err.data));
      }

      this.router.transitionTo('error-404');

      return {
        error: err,
      };
    }
  }

  async pageModel(params, transition, pageRes) {
    await fetchBuckets();
    try {
      const id = pageRes?.page?.id;

      this.bump(id);
      PagesApi.visited(id);

      fetchEntriesByTeller({
        id: id,
        tellerType: TellerType.PAGE,
      });

      const pageObj = getPage(id);

      if (pageObj.get('isOwnerAdmin')) {
        PagesApi.unseenCount({ id }).then(pick('unseenCount')).then(emberSet(pageObj));
      }

      if ((pageObj.get('isFollower') || pageObj.get('isOwnerAdmin')) && !ds.followed.items.find((i) => i.id === id)) {
        ds.followed.items.pushObject(pageObj);
      }

      if (!pageObj.get('isFollower')) {
        PagesApi.rejectInvitation(id);
      }

      return EmberObject.create({
        pageId: id,
        app: this.modelFor('app'),
        theme: this.theme,
        followed: ds.followed,
        page: getPage(id),
        entries: ds.entriesByTeller.for(id),
      });
    } catch (error) {
      this.handle404Error(transition, __('Sorry, the page cannot be found or it was unpublished'));
      this.handleFallbackRouting(getPage(id));
    }
  }

  administrated() {
    // fetch administrated every time
    return fetchAdministrated({ limit: 100 }); // currently administrated pages aren't paginated, we can only load 100 and have hope (MW)
  }

  bump(id) {
    let page = ds.followed.items.find((i) => i.id === id);
    if (page) {
      page.set('lastVisited', Date.now());
    }
  }

  async handle404Error(transition, msg) {
    // eslint-disable-next-line no-useless-catch
    try {
      const response = await transition.abort().catch((resp) => resp);
      if (response && response.status === 404) {
        FunctionalUtils.error(msg);
        throw response;
      }
    } catch (error) {
      throw error;
    }
  }

  handleFallbackRouting(page) {
    if (page) {
      PagesApi.rejectInvitation(page.id);
      removeItemIfExist(ds.followed, page.id);
    }
    this.router.transitionTo('app.pages');
  }

  @action
  didTransition() {
    this.dynamicDialogs.closeAll();

    if (this.context === 'profile') {
      // fix for SG-18925
      const dialogEl = document.querySelector('.dialog_wrapper');
      if (dialogEl) {
        dialogEl.style.top = 0;
      }

      window.scrollTo(0, 0);
    } else {
      return true;
    }
  }

  @action
  async reloadModel() {
    this.refresh();
  }

  activate() {
    if (this.context === 'page') {
      PS.Sub('page.notification.new', (data) => {
        emberSet(getPage(data.pageId), { unseenCount: data.unseenCount });
      });
    }
  }

  deactivate() {
    if (this.context === 'page') {
      PS.Unsub('page.notification.new', 'clearAll');
    }
  }
}
